import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { CONTACT_INFO } from "@/data/anabiya";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccessPage = () => {
  const location = useLocation();
  const q = new URLSearchParams(location.search);
  const orderId = q.get("order_id");
  const gateway = q.get("gateway");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API}/payments/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  return (
    <main data-testid="payment-success-page" className="bg-white min-h-screen">
      <AnnouncementBar />
      <Navbar />
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 size={28} strokeWidth={1.4} className="text-gold" />
            <span className="overline text-gold">Order confirmed</span>
          </div>
          <h1 className="font-display text-forest text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Thank you. <br />
            <span className="font-editorial italic text-champagne">Your order is with us.</span>
          </h1>
          <p className="mt-6 text-forest/75 text-lg max-w-xl">
            We&rsquo;ve received your payment via <span className="text-forest font-medium">{gateway || "our gateway"}</span>. Your order reference is:
          </p>
          <div className="mt-4 inline-block bg-bone border-hairline px-5 py-3">
            <div className="overline text-forest/60">Order</div>
            <div className="font-display text-forest text-2xl tabnum">{orderId || "—"}</div>
          </div>

          {order?.items?.length ? (
            <div className="mt-10 border-hairline p-6 max-w-2xl">
              <div className="overline text-gold mb-4">Details</div>
              <ul className="divide-y divide-hairline">
                {order.items.map((it, i) => (
                  <li key={i} className="py-3 flex justify-between items-center gap-4">
                    <span className="text-forest">
                      {it.title} <span className="text-forest/50">× {it.qty}</span>
                    </span>
                    <span className="tabnum">{it.line_total}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-3 border-t border-hairline flex justify-between">
                <span className="overline text-forest/60">Total · {order.currency}</span>
                <span className="font-display text-forest text-2xl tabnum">{order.subtotal}</span>
              </div>
            </div>
          ) : null}

          <p className="mt-10 text-forest/70 max-w-xl">
            You&rsquo;ll get a WhatsApp confirmation shortly with sample photos, expected dispatch date, and tracking. For anything urgent:
          </p>
          <div className="mt-5 flex flex-wrap gap-4">
            <a
              href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(`Hello Anabiya — order ${orderId}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 overline text-forest border border-forest/70 rounded-full px-5 py-3 hover:bg-forest hover:text-white transition-colors duration-500"
            >
              Message on WhatsApp →
            </a>
            <Link to="/shop" className="edit-link overline text-forest self-center">
              Keep browsing the shop →
            </Link>
          </div>
        </motion.div>
      </section>
      <Footer />
    </main>
  );
};

export const PaymentCancelPage = () => (
  <main data-testid="payment-cancel-page" className="bg-white min-h-screen">
    <AnnouncementBar />
    <Navbar />
    <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
      <h1 className="font-display text-forest text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-3xl">
        Payment cancelled. <br />
        <span className="font-editorial italic text-champagne">Your cart is still saved.</span>
      </h1>
      <p className="mt-6 text-forest/75 text-lg max-w-xl">
        No charge was made. You can pick up where you left off, try another method, or ping us on WhatsApp.
      </p>
      <div className="mt-8 flex flex-wrap gap-4">
        <Link
          to="/checkout"
          className="inline-flex items-center gap-2 bg-forest text-white overline px-5 py-3 rounded-full hover:bg-[hsl(150_60%_16%)] transition-colors duration-500"
        >
          Back to Checkout →
        </Link>
        <Link to="/shop" className="edit-link overline text-forest self-center">
          Continue shopping →
        </Link>
      </div>
    </section>
    <Footer />
  </main>
);
